import React, {useRef} from "react";
import {getTemplateDefinition} from "../data/template-catalog";
import {type BannerChange} from "./scope-adapter";
import {useLogoCycling} from "../hooks/useLogoCycling";
import {useBgData} from "../hooks/useBgData";
import {deriveBgUrl, useBgUrl} from "../hooks/useBgUrl";
import {useSetting} from "../contexts/setting";
import {useImages} from "../contexts/images";
import {workspaceClasses} from "../libs/workspaceClasses";
import {knownTemplates} from "../data/templates";
import {getProductBySlug} from "../data/products/index";
import {BannerScope} from "./BannerScope";
import {type BannerInstance} from "./document";

type Inspector = (root: React.RefObject<HTMLDivElement>) => React.ReactNode;
export function BannerRenderer({banner, onChange, renderMode = false, zoom = 3, inspector, onPendingChange, annotation}: {
  banner: BannerInstance; onChange: BannerChange; renderMode?: boolean; zoom?: number;
  onPendingChange?: (instanceId: string, count: number) => void; inspector?: Inspector; annotation?: React.ReactNode;
}) {
  const definition = getTemplateDefinition(banner.templateKey);
  const template = knownTemplates[definition.channel].find((entry) => entry.key === definition.key);
  const product = getProductBySlug(banner.product);
  if (!template || !product) throw new Error(`Unsupported banner: ${banner.templateKey}`);
  const needsGallery = banner.assets.background === null && Number(banner.appearance.backgroundOption) > 0
    && definition.channel !== "ads" && !definition.templateId.startsWith("SpeakerPhoto");
  const gallery = useBgData(definition.channel, banner.contentType, {enabled: needsGallery});
  const background = deriveBgUrl({bgOption: banner.appearance.backgroundOption, productValue: product.code, allBgUrls: gallery.allBgUrls});
  const error = needsGallery && gallery.isError ? `${banner.instanceId}: ${gallery.error.message}`
    : needsGallery && gallery.isSuccess && background.isMissing ? `${banner.instanceId}: Background ${banner.appearance.backgroundOption} is unavailable` : "";
  return (
    <BannerScope key={banner.templateKey} banner={banner} onChange={onChange} renderMode={renderMode} onPendingChange={onPendingChange}>
      <ScopedRenderer inspector={inspector} loading={needsGallery && gallery.isPending} error={error}>
        <BannerCanvas renderMode={renderMode}>
          {annotation}
          <div className={annotation ? undefined : "template-wrap"}>
            {template.getComponent.call({...template, id: banner.instanceId, zoom: renderMode ? 1 : zoom})}
          </div>
        </BannerCanvas>
      </ScopedRenderer>
    </BannerScope>
  );
}
function BannerCanvas({children, renderMode}: {children: React.ReactNode; renderMode: boolean}) {
  const {product, channel, layoutOption, bgOption, base} = useSetting();
  const {logoHidden} = useImages();
  const {url} = useBgUrl();
  const classes = workspaceClasses({backgroundUrl: url, product: product.value, channel: channel.value, backgroundOption: bgOption, layout: layoutOption});
  return <div className={`App ${classes}${renderMode ? " render-mode" : ""}${channel.value === "social" && logoHidden ? " logo-hidden-social" : ""}`}
    style={{"--base-color": `#${base}`} as React.CSSProperties}>{children}</div>;
}
function ScopedRenderer({children, inspector, loading, error}: {children: React.ReactNode; inspector?: Inspector; loading: boolean; error: string}) {
  const root = useRef<HTMLDivElement>(null);
  useLogoCycling(root);
  return <>{inspector?.(root)}<div ref={root} data-controlled-banner data-banner-loading={loading ? "true" : "false"}
    data-grid-resource-error={error || undefined}>{error && <p role="alert">{error}</p>}{children}</div></>;
}
